import { For, Show, createResource, createSignal } from 'solid-js';
import { api } from '../api';
import { ErrorLine, PageHeader, useAction } from '../ui';

type By = 'track' | 'rights_holder';
type Report = { from: string; to: string; by: By; items: any[]; totals: { plays: number; complete_plays: number; seconds: number } };

const thisMonth = () => new Date().toISOString().slice(0, 7);
const hours = (s: number) => (s >= 3600 ? `${(s / 3600).toFixed(1)} h` : `${Math.round(s / 60)} min`);
const num = (n: number) => n.toLocaleString('en-GB');

/** Operator: what played, per track or per rights holder, for a calendar month (UTC). */
export default function Reports() {
  const [month, setMonth] = createSignal(thisMonth());
  const [by, setBy] = createSignal<By>('track');
  const [report] = createResource(() => ({ m: month(), b: by() }),
    ({ m, b }) => api<Report>(`/reports/plays?month=${m}&by=${b}`));
  const csv = useAction();

  const download = () => csv.run(async () => {
    const r = await api<Response>(`/reports/plays?month=${month()}&by=${by()}`, { raw: true, headers: { Accept: 'text/csv' } });
    const url = URL.createObjectURL(await r.blob());
    const a = document.createElement('a'); a.href = url; a.download = `plays-${by()}-${month()}.csv`; a.click(); URL.revokeObjectURL(url);
  });

  return (
    <>
      <PageHeader title="Play reports" actions={
        <button class="btn btn-sm" disabled={csv.busy() || !report()?.items.length} onClick={download}>Export CSV</button>} />
      <div class="mb-3 flex flex-wrap items-center gap-3">
        <input type="month" class="input input-sm input-bordered" value={month()} max={thisMonth()} aria-label="Month"
          onChange={(e) => e.currentTarget.value && setMonth(e.currentTarget.value)} />
        <div class="join" role="group" aria-label="Group by">
          <button class={`btn join-item btn-sm ${by() === 'track' ? 'btn-primary' : ''}`} aria-pressed={by() === 'track'} onClick={() => setBy('track')}>Per track</button>
          <button class={`btn join-item btn-sm ${by() === 'rights_holder' ? 'btn-primary' : ''}`} aria-pressed={by() === 'rights_holder'} onClick={() => setBy('rights_holder')}>Per rights holder</button>
        </div>
        <Show when={report()}>{(r) =>
          <span class="text-sm text-ink-3">
            {num(r().totals.plays)} plays · {num(r().totals.complete_plays)} complete · {hours(r().totals.seconds)} played · times in UTC
          </span>}
        </Show>
      </div>
      <ErrorLine error={() => (report.error ? String(report.error.message ?? report.error) : csv.error())} />
      <div class="card-z overflow-x-auto p-0">
        <table class="table">
          <Show when={by() === 'track'} fallback={
            <thead><tr><th class="th">Rights holder</th><th class="th">Kind</th><th class="th text-right">Tracks</th>
              <th class="th text-right">Plays</th><th class="th text-right">Complete</th><th class="th text-right">Played</th><th class="th text-right">Zones</th></tr></thead>}>
            <thead><tr><th class="th">Track</th><th class="th">ISRC</th><th class="th">Rights holder</th><th class="th">Licence</th>
              <th class="th text-right">Plays</th><th class="th text-right">Complete</th><th class="th text-right">Played</th><th class="th text-right">Zones</th></tr></thead>
          </Show>
          <tbody>
            <For each={report.loading ? [] : report()?.items} fallback={
              <tr><td colspan="8" class="text-ink-3">{report.loading ? 'Loading…' : 'Nothing played in this month.'}</td></tr>}>
              {(r) => by() === 'track' ? (
                <tr>
                  <td><b>{r.title}</b> <span class="text-ink-3">{r.artist}</span></td>
                  <td class="mono text-xs">{r.isrc ?? '—'}</td>
                  <td>{r.rights_holder}</td>
                  <td>{String(r.licence).replace('_', ' ')}</td>
                  <td class="mono text-right">{num(r.plays)}</td>
                  <td class="mono text-right">{num(r.complete_plays)}</td>
                  <td class="mono text-right">{hours(r.seconds)}</td>
                  <td class="mono text-right">{r.zones}</td>
                </tr>
              ) : (
                <tr>
                  <td><b>{r.rights_holder}</b>
                    <Show when={!r.outside_collecting_societies}> <span class="badge badge-sm">collecting society</span></Show></td>
                  <td>{String(r.kind).replace('_', ' ')}</td>
                  <td class="mono text-right">{num(r.tracks)}</td>
                  <td class="mono text-right">{num(r.plays)}</td>
                  <td class="mono text-right">{num(r.complete_plays)}</td>
                  <td class="mono text-right">{hours(r.seconds)}</td>
                  <td class="mono text-right">{r.zones}</td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </div>
    </>
  );
}
